import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Check, LockKeyhole, Mail, RefreshCw } from "lucide-react";

const HouseholdApp = lazy(() => import("./App"));
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const TURNSTILE_SCRIPT_ID = "fam-turnstile-script";

type GateState = "checking" | "authorized" | "locked" | "unavailable";
type RequestState = "idle" | "sending" | "sent" | "error";

interface TurnstileOptions {
  action: string;
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  sitekey: string;
  theme: "auto";
}

interface TurnstileApi {
  remove(widgetId: string): void;
  render(container: HTMLElement, options: TurnstileOptions): string;
  reset(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    const existing = document.getElementById(
      TURNSTILE_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");

    const ready = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("Turnstile loaded without an API."));
    };
    script.addEventListener("load", ready, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile could not be loaded.")),
      { once: true },
    );

    if (!existing) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
  const loadPromise = pending.catch((error: unknown) => {
    turnstileScriptPromise = undefined;
    throw error;
  });
  turnstileScriptPromise = loadPromise;

  return loadPromise;
}

export default function AuthGate() {
  const [state, setState] = useState<GateState>("checking");

  const checkSession = useCallback(async () => {
    setState("checking");
    try {
      const response = await fetch(`${API_BASE}/api/v1/me`, {
        credentials: "include",
        headers: { accept: "application/json" },
      });
      if (response.ok) {
        setState("authorized");
        return;
      }
      setState(
        response.status === 401 || response.status === 403
          ? "locked"
          : "unavailable",
      );
    } catch {
      setState("unavailable");
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  if (state === "authorized") {
    return (
      <Suspense fallback={<GateStatus label="Opening Fam" />}>
        <HouseholdApp />
      </Suspense>
    );
  }

  if (state === "checking") return <GateStatus label="Checking access" />;

  if (state === "unavailable") {
    return (
      <GateShell>
        <LockKeyhole size={24} aria-hidden="true" />
        <p className="eyebrow">Private household workspace</p>
        <h1>Fam is unavailable.</h1>
        <p>
          The secure service could not be reached. Try the connection again.
        </p>
        <button
          className="auth-gate__retry"
          type="button"
          onClick={() => void checkSession()}
        >
          <RefreshCw size={17} aria-hidden="true" />
          Check connection
        </button>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <LockKeyhole size={24} aria-hidden="true" />
      <p className="eyebrow">Private household workspace</p>
      <h1>Sign in to Fam</h1>
      <p>Use the email address on your household invitation.</p>
      <MagicLinkForm />
    </GateShell>
  );
}

function MagicLinkForm() {
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [token, setToken] = useState("");
  const [requestState, setRequestState] = useState<RequestState>("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const resetTurnstile = useCallback(() => {
    setToken("");
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (!TURNSTILE_SITE_KEY || !turnstileContainer.current) {
      setRequestState("error");
      setStatusMessage("Secure sign-in is not configured yet.");
      return;
    }

    void loadTurnstile()
      .then((turnstile) => {
        if (!active || !turnstileContainer.current) return;
        widgetId.current = turnstile.render(turnstileContainer.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "auth_magic_link",
          theme: "auto",
          callback: (nextToken) => {
            setToken(nextToken);
            setRequestState("idle");
            setStatusMessage("");
          },
          "expired-callback": () => {
            setToken("");
            setStatusMessage("Verification expired. Please try again.");
          },
          "error-callback": () => {
            setToken("");
            setRequestState("error");
            setStatusMessage("Verification could not be completed.");
          },
        });
      })
      .catch(() => {
        if (!active) return;
        setRequestState("error");
        setStatusMessage("Verification could not be loaded.");
      });

    return () => {
      active = false;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const email = String(form.get("email") ?? "").trim();
    if (!email || !token) return;

    setRequestState("sending");
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/auth/sign-in/magic-link`, {
        method: "POST",
        credentials: "include",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          "x-fam-turnstile-token": token,
        },
        body: JSON.stringify({
          email,
          callbackURL: window.location.origin,
          errorCallbackURL: window.location.origin,
        }),
      });

      if (!response.ok) throw new Error("Magic-link request failed.");
      formElement.reset();
      setRequestState("sent");
      setStatusMessage(
        "If this address is approved, a sign-in link is on its way.",
      );
    } catch {
      setRequestState("error");
      setStatusMessage("Sign-in could not be started. Please try again.");
    } finally {
      resetTurnstile();
    }
  };

  const isSending = requestState === "sending";

  return (
    <form className="auth-gate__form" onSubmit={(event) => void submit(event)}>
      <label htmlFor="fam-email">Email address</label>
      <div className="auth-gate__input-wrap">
        <Mail size={18} aria-hidden="true" />
        <input
          id="fam-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          required
          disabled={isSending}
        />
      </div>
      <div
        className="auth-gate__turnstile"
        ref={turnstileContainer}
        aria-label="Human verification"
      />
      <button
        className="auth-gate__submit"
        type="submit"
        disabled={isSending || !token}
      >
        <Mail size={17} aria-hidden="true" />
        {isSending ? "Sending link" : "Email me a sign-in link"}
      </button>
      <div className="auth-gate__status" aria-live="polite">
        {requestState === "sent" && <Check size={17} aria-hidden="true" />}
        <span>{statusMessage}</span>
      </div>
    </form>
  );
}

function GateShell({ children }: { children: ReactNode }) {
  return (
    <main className="auth-gate">
      <div className="auth-gate__brand" aria-label="Fam">
        F
      </div>
      <div className="auth-gate__content">{children}</div>
      <small>Private by default. Shared intentionally.</small>
    </main>
  );
}

function GateStatus({ label }: { label: string }) {
  return (
    <main className="auth-gate auth-gate--status" aria-live="polite">
      <div className="auth-gate__brand" aria-hidden="true">
        F
      </div>
      <p>{label}</p>
    </main>
  );
}
